import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  TextField,
  Button,
  Typography,
  Container,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { debounce } from 'lodash';
import { fetchServerTime } from '../utils/dateTime';
import FormCard from './FormCard';

function MoradorFormV2({ onMoradorAdded, initialData, onCancelEdit }) {
  const { user } = useAuth();
  const [empresa_id, setEmpresaId] = useState('');
  const [imovel_id, setImovelId] = useState('');
  const [pessoa_id, setPessoaId] = useState('');
  const [is_inquilino, setIsInquilino] = useState(false);
  const [data_entrada, setDataEntrada] = useState('');
  const [data_saida, setDataSaida] = useState('');
  const [empresas, setEmpresas] = useState([]);

  const [imovelOptions, setImovelOptions] = useState([]);
  const [loadingImoveis, setLoadingImoveis] = useState(false);
  const [selectedImovelObject, setSelectedImovelObject] = useState(null);

  const [pessoaOptions, setPessoaOptions] = useState([]);
  const [loadingPessoas, setLoadingPessoas] = useState(false);
  const [selectedPessoaObject, setSelectedPessoaObject] = useState(null);

  const debouncedFetchImoveis = useMemo(() => debounce((searchTerm) => {
      setLoadingImoveis(true);
      api.get(`/imoveis?search=${encodeURIComponent(searchTerm)}&page=0&limit=20`)
        .then((response) => setImovelOptions(response.data.data || response.data))
        .catch((error) => console.error('Erro ao buscar imoveis:', error))
        .finally(() => setLoadingImoveis(false));
    }, 500),
    []
  );

  const debouncedFetchPessoas = useMemo(() => debounce((searchTerm) => {
      setLoadingPessoas(true);
      api.get(`/pessoas?search=${searchTerm}`)
        .then((response) => setPessoaOptions(response.data.data || response.data))
        .catch((error) => console.error('Erro ao buscar pessoas:', error))
        .finally(() => setLoadingPessoas(false));
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedFetchImoveis.cancel();
      debouncedFetchPessoas.cancel();
    };
  }, [debouncedFetchImoveis, debouncedFetchPessoas]);

  useEffect(() => {
    if (user && user.tipo_usuario === 'admin') {
      api.get('/empresas')
        .then((response) => setEmpresas(response.data.data || response.data))
        .catch((error) => console.error('Erro ao buscar empresas:', error));
    }

    if (initialData) {
      setEmpresaId(initialData.empresa_id);
      setImovelId(initialData.imovel_id);
      setPessoaId(initialData.pessoa_id);
      setIsInquilino(!!initialData.is_inquilino);
      setDataEntrada(initialData.data_entrada ? initialData.data_entrada.slice(0, 10) : '');
      setDataSaida(initialData.data_saida ? initialData.data_saida.slice(0, 10) : '');

      if (initialData.imovel_id) {
        api.get(`/imoveis?id=${initialData.imovel_id}`)
          .then((response) => {
            const imovel = (Array.isArray(response.data.data) ? response.data.data[0] : response.data.data) || response.data[0] || response.data;
            if (imovel) {
              setSelectedImovelObject(imovel);
              setImovelOptions([imovel]);
            }
          })
          .catch((error) => console.error('Erro ao buscar imovel inicial:', error));
      }

      if (initialData.pessoa_id) {
        api.get(`/pessoas?id=${initialData.pessoa_id}`)
          .then((response) => {
            const pessoa = (Array.isArray(response.data.data) ? response.data.data[0] : response.data.data) || response.data[0] || response.data;
            if (pessoa) {
              setSelectedPessoaObject(pessoa);
              setPessoaOptions([pessoa]);
            }
          })
          .catch((error) => console.error('Erro ao buscar pessoa inicial:', error));
      }
    } else {
      clearForm();
      fetchServerTime(api)
        .then((data) => setDataEntrada(data.empresaDate || ''))
        .catch((error) => console.error('Erro ao buscar data atual do servidor:', error));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, user]);

  const clearForm = () => {
    setEmpresaId('');
    setImovelId('');
    setPessoaId('');
    setIsInquilino(false);
    setDataEntrada('');
    setDataSaida('');
    setSelectedImovelObject(null);
    setSelectedPessoaObject(null);
    setImovelOptions([]);
    setPessoaOptions([]);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const moradorData = {
      empresa_id: user.tipo_usuario === 'admin' ? empresa_id : user.empresa_id,
      imovel_id,
      pessoa_id,
      is_inquilino,
      data_entrada,
      data_saida: data_saida || null,
    };

    if (initialData) {
      api.put(`/moradores/${initialData.id}`, moradorData)
        .then(() => {
          clearForm();
          onMoradorAdded();
          onCancelEdit();
        })
        .catch((error) => {
          console.error('Erro ao atualizar morador:', error);
        });
    } else {
      api.post('/moradores', moradorData)
        .then(() => {
          clearForm();
          onMoradorAdded();
        })
        .catch((error) => {
          console.error('Erro ao adicionar morador:', error);
        });
    }
  };

  return (
    <Container maxWidth="sm">
      <FormCard component="form" onSubmit={handleSubmit}>
        <Typography variant="h5" gutterBottom>
          {initialData ? 'Editar Morador' : 'Adicionar Novo Morador'}
        </Typography>

        {(user && user.tipo_usuario === 'admin') && (
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="empresa-select-label">Empresa</InputLabel>
            <Select
              labelId="empresa-select-label"
              value={empresa_id}
              label="Empresa"
              onChange={(e) => setEmpresaId(e.target.value)}
              disabled={!!initialData}
            >
              {empresas.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Autocomplete
          id="imovel-autocomplete"
          options={imovelOptions}
          getOptionLabel={(option) => `${option.descricao} - ${option.numero}` || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedImovelObject}
          onChange={(event, newValue) => {
            setSelectedImovelObject(newValue);
            setImovelId(newValue ? newValue.id : '');
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              if (newInputValue) {
                debouncedFetchImoveis(newInputValue);
              } else {
                setImovelOptions([]);
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Imovel"
              variant="outlined"
              margin="normal"
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingImoveis ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          disabled={!!initialData}
        />

        <Autocomplete
          id="pessoa-autocomplete"
          options={pessoaOptions}
          getOptionLabel={(option) => option.nome || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedPessoaObject}
          onChange={(event, newValue) => {
            setSelectedPessoaObject(newValue);
            setPessoaId(newValue ? newValue.id : '');
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              if (newInputValue) {
                debouncedFetchPessoas(newInputValue);
              } else {
                setPessoaOptions([]);
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Pessoa"
              variant="outlined"
              margin="normal"
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPessoas ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          disabled={!!initialData}
        />

        <TextField
          label="Data de Entrada"
          type="date"
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={data_entrada}
          onChange={(e) => setDataEntrada(e.target.value)}
          required
        />

        <TextField
          label="Data de Saida"
          type="date"
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={data_saida}
          onChange={(e) => setDataSaida(e.target.value)}
        />

        <FormControlLabel
          control={<Checkbox checked={is_inquilino} onChange={(e) => setIsInquilino(e.target.checked)} />}
          label="E Inquilino?"
        />

        <Typography variant="body2" sx={{ mt: 1 }}>
          Situacao atual: {data_saida ? 'Nao esta morando' : 'Esta morando'}
        </Typography>

        <Button type="submit" variant="contained" color="primary" sx={{ mt: 2, mr: 2 }}>
          {initialData ? 'Atualizar Morador' : 'Adicionar Morador'}
        </Button>
        <Button type="button" variant="outlined" color="secondary" sx={{ mt: 2 }} onClick={onCancelEdit}>
          Cancelar
        </Button>
      </FormCard>
    </Container>
  );
}

export default MoradorFormV2;

